/**
 * RiskAssessmentSettingsView.js
 *
 * Full-screen settings view for Risk Assessment module configuration.
 * ISM Code / WBC3 Edition: 7 settings for risk assessment management
 *
 * Settings:
 * 1. Default Review Frequency - annually, biannually, quarterly, monthly
 * 2. Due Soon Warning Days - Days before review to show "Due Soon" (7-90)
 * 3. Show ALARP Guidance - Display ALARP zone guidance on assessments
 * 4. Require Action Plan for High Risk - ISM Code 1.2.2.2 compliance
 * 5. Show Risk Matrix on PDF - Include in exports
 * 6. Show Control Hierarchy on PDF - Include in exports
 * 7. Require Digital Signatures - ISM Code 12.2 compliance
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as DrillSettingsView)
 */

import React, { useState } from 'react';
import {
  ArrowLeft,
  Clock,
  Bell,
  AlertTriangle,
  FileText,
  Download,
  PenTool,
  Info,
  RotateCcw,
  Check,
  Shield,
  Activity
} from 'lucide-react';
import { useRiskAssessmentConfig, REVIEW_FREQUENCY_OPTIONS } from '../../../hooks/domain/risk/useRiskAssessmentConfig';

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children, regulatory = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-slate-700 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {regulatory && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                ISM
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  // Sync when external value changes
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Select dropdown component
 */
const SelectInput = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-3 py-2 text-sm border border-slate-300 rounded-lg
      focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
      bg-white cursor-pointer"
  >
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>
        {opt.label} - {opt.description}
      </option>
    ))}
  </select>
);

/**
 * Main RiskAssessmentSettingsView component
 */
const RiskAssessmentSettingsView = ({ onBack }) => {
  const {
    defaultReviewFrequency,
    dueSoonWarningDays,
    showAlarpGuidance,
    requireActionPlanForHighRisk,
    showRiskMatrixOnPdf,
    showControlHierarchyOnPdf,
    requireDigitalSignatures,
    disabledRegulatorySettings,
    setDefaultReviewFrequency,
    setDueSoonWarningDays,
    setShowAlarpGuidance,
    setRequireActionPlanForHighRisk,
    setShowRiskMatrixOnPdf,
    setShowControlHierarchyOnPdf,
    setRequireDigitalSignatures,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useRiskAssessmentConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 -ml-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Risk Assessment Settings</h1>
                <p className="text-sm text-slate-500">Configure risk assessment requirements for this vessel</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all risk assessment settings to their default values, including re-enabling all compliance features. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Regulatory Warning */}
        {disabledRegulatorySettings?.length > 0 && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Regulatory Settings Disabled</p>
              <p className="mt-0.5">
                You have disabled {disabledRegulatorySettings.length} ISM Code compliance setting(s): {' '}
                <strong>{disabledRegulatorySettings.join(', ')}</strong>.
                This may affect audit compliance.
              </p>
            </div>
          </div>
        )}

        {/* Section 1: Default Review Frequency */}
        <SettingsSection
          icon={Clock}
          title="Default Review Frequency"
          description="Set the default review period for new assessments"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              New risk assessments will use this review frequency. ISM Code requires periodic review of risk assessments.
            </p>
            <SelectInput
              value={defaultReviewFrequency}
              onChange={setDefaultReviewFrequency}
              options={REVIEW_FREQUENCY_OPTIONS}
            />
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Guidance:</strong> Annual reviews are recommended for most assessments.
                High-risk activities (ALARP/Intolerable zone) should be reviewed more frequently.
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* Section 2: Due Soon Warning Days */}
        <SettingsSection
          icon={Bell}
          title="Due Soon Warning"
          description="When to show upcoming review warnings"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Show "Due Soon" warning when an assessment review is due within this many days.
            </p>
            <NumberInput
              value={dueSoonWarningDays}
              onChange={setDueSoonWarningDays}
              min={7}
              max={90}
              unit="days"
            />
          </div>
        </SettingsSection>

        {/* Section 3: ALARP Guidance */}
        <SettingsSection
          icon={Activity}
          title="ALARP Zone Guidance"
          description="Display risk reduction guidance on assessments"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Show ALARP Guidance</p>
              <p className="text-sm text-slate-500 mt-1">
                Display "As Low As Reasonably Practicable" guidance on risk assessments to help
                users understand risk reduction requirements.
              </p>
            </div>
            <Toggle
              enabled={showAlarpGuidance}
              onChange={setShowAlarpGuidance}
            />
          </div>
        </SettingsSection>

        {/* Section 4: Require Action Plan for High Risk */}
        <SettingsSection
          icon={AlertTriangle}
          title="Require Action Plan for High Risk"
          description="Enforce action plans for ALARP and Intolerable risk levels"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enforce Action Plan Requirement</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, risk assessments with High, Extreme, or ALARP zone risks must include
                documented action plans for risk reduction.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> ISM Code Section 1.2.2.2 requires that hazards are
                  assessed and appropriate safeguards established. Disabling this setting may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireActionPlanForHighRisk}
              onChange={setRequireActionPlanForHighRisk}
            />
          </div>
        </SettingsSection>

        {/* Section 5 & 6: PDF Export Options */}
        <SettingsSection
          icon={Download}
          title="PDF Export Options"
          description="Configure what's included in exported risk assessments"
        >
          <div className="space-y-4">
            {/* Risk Matrix */}
            <div className="flex items-start justify-between gap-4 py-2">
              <div>
                <p className="text-sm font-medium text-slate-700">Include Risk Matrix</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Include the 5x5 risk matrix visualization in PDF exports.
                </p>
              </div>
              <Toggle
                enabled={showRiskMatrixOnPdf}
                onChange={setShowRiskMatrixOnPdf}
              />
            </div>

            {/* Control Hierarchy */}
            <div className="flex items-start justify-between gap-4 py-2 border-t border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-700">Include Control Hierarchy</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  Include the ISO 45001 hierarchy of controls diagram in PDF exports.
                </p>
              </div>
              <Toggle
                enabled={showControlHierarchyOnPdf}
                onChange={setShowControlHierarchyOnPdf}
              />
            </div>
          </div>
        </SettingsSection>

        {/* Section 7: Require Digital Signatures */}
        <SettingsSection
          icon={PenTool}
          title="Require Digital Signatures"
          description="Enforce signature requirements for risk assessment approval"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Require Signatures for Approval</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, risk assessments require digital signatures from the assessor
                and approver before being marked as Active.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> ISM Code Section 12.2 requires documented procedures
                  including verification and approval. Disabling this setting may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireDigitalSignatures}
              onChange={setRequireDigitalSignatures}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessmentSettingsView;
